import ClassNames from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import Switch from '../form/switch/switch';
import Icon from '../icons/Icon/Icon';
import Loader from '../loader/loader';

/**
 * A component for displaying content in a defined panel container. If `noBodyPadding=true` and you would like the standard panel padding around elements, add class `panel-padding` somewhere in the panel's body.
 */

class Panel extends React.Component {
  constructor(props) {
    super(props);

    const { defaultExpanded, hasExpandableContent } = this.props;

    this.state = {
      expanded: defaultExpanded && hasExpandableContent,
    };

    this.bodyContainer = React.createRef();
  }

  componentDidMount() {
    const body = this.bodyContainer.current;
    const { expanded } = this.state;
    const { hasExpandableContent } = this.props;
    if (expanded) {
      body.style.maxHeight = '2000px';
    } else if (hasExpandableContent) {
      body.style.maxHeight = '0';
    }
  }

  componentDidUpdate() {
    const { expanded } = this.state;
    if (expanded) {
      const body = this.bodyContainer?.current;
      if (body) {
        body.style.maxHeight = '2000px';
      }
    }
  }

  onTransitionEnd = () => {
    const body = this.bodyContainer.current;
    body.removeEventListener('transitionend', this.onTransitionEnd);
  };

  handleSwitchClick = () => {
    const { onToggleExpand } = this.props;
    const { expanded } = this.state;
    const body = this.bodyContainer.current;
    body.addEventListener('transitionend', this.onTransitionEnd);

    if (onToggleExpand) {
      onToggleExpand({ expanded });
    }

    if (expanded) {
      this.collapseBody();
    } else {
      this.expandBody();
    }
  };

  collapseBody = () => {
    this.setState({ expanded: false });
    const body = this.bodyContainer.current;

    // as soon as the previous style change has taken effect, have the element transition to height: 0
    const shrinkTimer = window.requestAnimationFrame(() => {
      body.style.maxHeight = '0';
    });
    return () => window.cancelAnimationFrame(shrinkTimer);
  };

  expandBody = () => {
    this.setState({ expanded: true });
    const body = this.bodyContainer.current;

    // explicitly setting the height will trigger the transition
    body.style.maxHeight = '2000px';
  };

  render() {
    const {
      backPanelContent,
      bodyClasses,
      children,
      classes,
      dark,
      double,
      footer,
      footerClasses,
      fullWidth,
      hasExpandableContent,
      headerBorder,
      headerButtons,
      headerCenterContent,
      icon,
      iconSvgProps,
      iconSize,
      isLoading,
      largeTitle,
      noBodyPadding,
      noTitleLeftPadding,
      noTitleRightPadding,
      smallTitle,
      title,
      titleWell,
      type,
      well,
    } = this.props;
    const { expanded } = this.state;

    const hasTitle = !!icon || !!title || !!headerCenterContent || !!headerButtons;
    let svgProps = iconSvgProps;
    if (dark) {
      svgProps = { style: { fill: '#ffffff' } };
    }

    const panel = (
      <div
        className={ClassNames('Panel', classes, {
          'Panel--double': double,
          [`Panel--${type}`]: type,
          'Panel--dark': dark,
          'Panel--full-width': fullWidth,
        })}
      >
        <Loader classes="Loader--panel" isLoading={isLoading} />
        {hasTitle && (
          <>
            <div
              className={ClassNames('Panel__title grid-row', {
                'Panel__title--header-border': headerBorder,
                'Panel__title--large': largeTitle,
                'Panel__title--small': smallTitle,
                'Panel__title--no-left-padding': noTitleLeftPadding,
                'Panel__title--no-right-padding': noTitleRightPadding,
              })}
            >
              {icon && (
                <Icon
                  classes="Panel__title__icon"
                  name={icon}
                  size={iconSize}
                  svgProps={svgProps}
                />
              )}
              {title && (
                <div className="grid-item">
                  {title}
                  {hasExpandableContent && (
                    <div className="Panel__title__switch">
                      <Switch checked={expanded} onChange={this.handleSwitchClick} />
                    </div>
                  )}
                </div>
              )}
              {headerCenterContent && headerCenterContent}
              {headerButtons && headerButtons}
            </div>
            {titleWell && <div className="Panel__title__well">{titleWell}</div>}
          </>
        )}
        <div
          className={ClassNames('Panel__body', bodyClasses, {
            'Panel__body--no-padding': noBodyPadding || hasExpandableContent,
            'Panel__body--has-expandable-content': hasExpandableContent,
          })}
          ref={this.bodyContainer}
        >
          <div className="Panel__body--height-calculator">
            {well ? <div className="Panel__well">{children}</div> : children}
          </div>
        </div>
        {footer && <div className={ClassNames('Panel__footer', footerClasses)}>{footer}</div>}
      </div>
    );
    return double ? (
      <div className="Panel__double-wrapper">
        {panel}
        <div
          className={ClassNames('Panel', 'Panel--double', 'Panel--double-back', {
            'Panel--dark': dark,
          })}
        >
          {backPanelContent}
        </div>
      </div>
    ) : (
      panel
    );
  }
}

Panel.propTypes = {
  /**
   * When rendering a double panel, this is the content on the secondary panel displayed below the main panel
   */
  backPanelContent: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
   * A space-separated list of classes to apply to the `div` wrapping the children inside the panel body
   */
  bodyClasses: PropTypes.string,
  /**
   * The main content of the panel displayed in the panel body
   */
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
   * A space-separated list of classes to apply to the `div` wrapping the whole panel component
   */
  classes: PropTypes.string,
  /**
   * Set to `true` to apply dark mode styles to the panel
   */
  dark: PropTypes.bool,
  /**
   * Set to `true` to have the Panel's body content visible when component loads (this has no effect if `hasExpandableContent` is set to false)
   */
  defaultExpanded: PropTypes.bool,
  /**
   * Set to `true` to display as a double panel
   */
  double: PropTypes.bool,
  /**
   * Content to display in panel footer
   */
  footer: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
   * A space-separated list of classes to apply to the footer container (`div`)
   */
  footerClasses: PropTypes.string,
  /**
   * Set to `true` to make the panel the full width of its container
   */
  fullWidth: PropTypes.bool,
  /**
   * Set to `true` to show the `Switch` component in the header and hide the Panel's body content when `Switch` is off. Requires a `title` in order to render the `Switch`
   */
  hasExpandableContent: PropTypes.bool,
  /**
   * Set to `true` to include a border between the header/title content (includes `icon`, `title`, `headerCenterContent`, and/or `headerButtons`) and the main panel body content (`children`)
   */
  headerBorder: PropTypes.bool,
  /**
   * Include a node to render a button(s) or other component(s) in the upper right of the panel.
   * These are rendered inside the header/title section of the panel, so are not affected by
   * `bodyClasses`.
   */
  headerButtons: PropTypes.node,
  /**
   * Content displayed in the middle section of the panel header. Text will be formatted the same way as the panel title unless otherwise specified.
   */
  headerCenterContent: PropTypes.node,
  /**
   * Name of icon to display next to panel title
   */
  icon: PropTypes.string,
  /**
   * Apply props to the title icon `svg` (these are overridden if panel is in dark mode)
   */
  // eslint-disable-next-line react/forbid-prop-types
  iconSvgProps: PropTypes.object,
  /**
   * Major, minor, or micro (Major is default)
   */
  iconSize: PropTypes.string,
  /**
   * Panel loading state is indicated with the animated Loader component at the top of the
   * container
   */
  isLoading: PropTypes.bool,
  /**
   * Set to `true` to use large header styles (28px)
   */
  largeTitle: PropTypes.bool,
  /**
   * Set to `true` to remove all padding from the main panel body
   */
  noBodyPadding: PropTypes.bool,
  /**
   * Set to `true` to remove all left side padding from the title/header section of panel
   */
  noTitleLeftPadding: PropTypes.bool,
  /**
   * Set to `true` to remove all right side padding from the title/header section of panel
   */
  noTitleRightPadding: PropTypes.bool,
  /**
   * Callback function to run when the value of the Switch component changes (this has no effect if `hasExpandableContent` is set to `false`). The arg passed into this function is the `{expanded}` state of the panel BEFORE the switch triggers the change. (We may want to change this in the future to pass the final updated state, but this affects some current implementations in dashboard that would need to be updated)
   */
  onToggleExpand: PropTypes.func,
  /**
   * Set to `true` to use small header styles (12px)
   */
  smallTitle: PropTypes.bool,
  /**
   * The content on the left side of the header/title section
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * The content to display in a well below the title section (will not show if there is no title)
   */
  titleWell: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Available style types include: `blue`, `padded`, `success`, `warn` (case sensitive)
   */
  type: PropTypes.string,
  /**
   * Set to `true` to display body content inside a well
   */
  well: PropTypes.bool,
};

Panel.defaultProps = {
  backPanelContent: null,
  bodyClasses: null,
  children: null,
  classes: '',
  defaultExpanded: true,
  double: false,
  footer: null,
  footerClasses: '',
  fullWidth: false,
  headerBorder: true,
  headerButtons: null,
  headerCenterContent: null,
  icon: null,
  iconSvgProps: null,
  iconSize: 'major',
  isLoading: false,
  largeTitle: false,
  noBodyPadding: false,
  noTitleLeftPadding: false,
  noTitleRightPadding: false,
  title: '',
  type: null,
  well: false,
  dark: undefined,
  hasExpandableContent: undefined,
  onToggleExpand: undefined,
  smallTitle: undefined,
  titleWell: undefined,
};

export default Panel;
